/**
 * Zod schemas for the match state and its parts. They are typed against the interfaces in
 * `types.ts`, so a field added to one without the other fails to compile. Used at the network
 * boundary (Phase 4 snapshots), by the resume-from-JSON replay proof, and by the client's URL and
 * debug inputs.
 */
import { z } from 'zod';
import { PICKUP_KINDS } from '../arena/schema.js';
import { LoadoutSchema } from '../loadout/schemas.js';
import { MechInputSchema, MechStateSchema, Vec3Schema } from '../sim/schemas.js';
import { BalanceOverridesSchema } from '../data/overrides-schema.js';
const finite = z.number().finite();
const int = z.number().int();
const nonNegInt = int.nonnegative();
export const SlotSchema = z.union([z.literal(0), z.literal(1)]);
export const BotLevelSchema = z.enum(['idle', 'easy', 'normal', 'hard']);
export const MatchModeSchema = z.enum(['practice', 'botFill', 'casual', 'ranked']);
export const HitLocationSchema = z.enum(['legs', 'torso', 'arms']);
export const DamageKindSchema = z.enum([
    'hitscan',
    'projectile',
    'beam',
    'melee',
    'splash',
    'ability',
    'burn',
    'drain',
    'turret',
    'field',
    'suddenDeath',
    'hazard',
    'execute',
]);
export const DeployableKindSchema = z.enum(['barrier', 'turret', 'drone', 'field', 'marker']);
export const RoundEndReasonSchema = z.enum(['kill', 'time', 'suddenDeath', 'mutual', 'forfeit']);
export const FlowPhaseSchema = z.enum(['countdown', 'live', 'suddenDeath', 'roundEnd', 'matchEnd']);
export const BotModeSchema = z.enum(['approach', 'strafe', 'retreat', 'cover', 'hold']);
export const EffectFlagSchema = z.enum([
    'noBoost',
    'noJump',
    'noAbilities',
    'noShieldRegen',
    'noWeapons',
    'immobile',
    'noTurn',
    'ccImmune',
    'cloaked',
    'revealed',
    'invulnerable',
    'heatLock',
    'noUltCharge',
    'stunned',
]);
export const SpawnPointSchema = z
    .object({ position: Vec3Schema, yaw: finite })
    .strict();
export const MatchConfigSchema = z
    .object({
    mode: MatchModeSchema,
    roundSeconds: finite.positive(),
    countdownSeconds: finite.nonnegative(),
    roundEndSeconds: finite.nonnegative(),
    roundsToWin: int.positive(),
    bots: z.tuple([BotLevelSchema.nullable(), BotLevelSchema.nullable()]),
})
    .strict();
export const MatchSetupSchema = z
    .object({
    seed: int,
    arenaId: z.string().min(1),
    mechs: z.tuple([z.string().min(1), z.string().min(1)]),
    spawns: z.tuple([SpawnPointSchema, SpawnPointSchema]),
    config: z
        .object({
        mode: MatchModeSchema,
        roundSeconds: finite.positive(),
        countdownSeconds: finite.nonnegative(),
        roundEndSeconds: finite.nonnegative(),
        roundsToWin: int.positive(),
        bots: z.tuple([BotLevelSchema.nullable(), BotLevelSchema.nullable()]),
    })
        .strict()
        .partial()
        .optional(),
    loadouts: z.tuple([LoadoutSchema.nullable(), LoadoutSchema.nullable()]).optional(),
    balance: z
        .object({ version: int.nonnegative(), overrides: BalanceOverridesSchema })
        .strict()
        .optional(),
})
    .strict();
export const WeaponRuntimeSchema = z
    .object({
    id: z.string().min(1),
    cooldown: finite,
    ammo: nonNegInt.nullable(),
    reloadTicks: nonNegInt,
    spin: finite.min(0).max(1),
    bloom: finite.min(0).max(1),
    burstLeft: nonNegInt,
    beamTicks: nonNegInt,
    beamCoolTicks: nonNegInt,
    held: z.boolean(),
})
    .strict();
export const MeleeRuntimeSchema = z
    .object({
    cooldown: finite,
    phase: z.enum(['idle', 'windup', 'active', 'recovery']),
    ticks: nonNegInt,
    hit: z.boolean(),
})
    .strict();
export const AbilityRuntimeSchema = z
    .object({
    cooldown: finite,
    active: z.boolean(),
    step: nonNegInt,
    ticks: nonNegInt,
    toggled: z.boolean(),
    held: z.boolean(),
    target: Vec3Schema.nullable(),
})
    .strict();
export const EffectParamsSchema = z
    .object({
    speedScale: finite.optional(),
    turnScale: finite.optional(),
    damageScale: finite.optional(),
    damageTakenScale: finite.optional(),
    fireRateScale: finite.optional(),
    heatScale: finite.optional(),
    dissipationScale: finite.optional(),
    spreadScale: finite.optional(),
    armorBonus: finite.optional(),
    cooldownScale: finite.optional(),
    jetThrustScale: finite.optional(),
    jetCostScale: finite.optional(),
    flags: z.array(EffectFlagSchema).optional(),
    dash: z
        .object({
        vx: finite,
        vz: finite,
        damage: finite,
        knockback: finite,
        stopOnHit: z.boolean(),
        hit: z.boolean(),
    })
        .strict()
        .optional(),
    dot: z.object({ damagePerSecond: finite, kind: DamageKindSchema }).strict().optional(),
    hot: z
        .object({
        hp: finite.optional(),
        shield: finite.optional(),
        heat: finite.optional(),
        energy: finite.optional(),
    })
        .strict()
        .optional(),
    parry: z.object({ arcDeg: finite, stunTicks: nonNegInt }).strict().optional(),
    cloak: z.object({ revealDistance: finite, breakOnFire: z.boolean() }).strict().optional(),
    impulse: Vec3Schema.optional(),
    burst: z
        .object({
        weapon: z.string().min(1),
        left: nonNegInt,
        intervalTicks: nonNegInt,
        timer: int,
        pattern: z.enum(['aim', 'swarmUp', 'mortar']),
        spreadDeg: finite,
        scatter: finite,
        target: Vec3Schema.nullable(),
    })
        .strict()
        .optional(),
    beam: z
        .object({ weapon: z.string().min(1), hpPerSecond: finite, energyPerSecond: finite })
        .strict()
        .optional(),
})
    .strict();
export const StatusEffectSchema = z
    .object({
    id: nonNegInt,
    kind: z.string().min(1),
    source: SlotSchema,
    ticksLeft: nonNegInt,
    params: EffectParamsSchema,
})
    .strict();
export const ProjectileSchema = z
    .object({
    id: nonNegInt,
    slot: SlotSchema,
    weapon: z.string().min(1),
    position: Vec3Schema,
    velocity: Vec3Schema,
    ticksLeft: nonNegInt,
    damageScale: finite,
    homingTarget: SlotSchema.nullable(),
    chainsLeft: nonNegInt,
    reflected: z.boolean(),
    groundY: finite.nullable(),
})
    .strict();
export const DeployableParamsSchema = z
    .object({
    halfExtents: Vec3Schema.optional(),
    weapon: z.string().optional(),
    range: finite.optional(),
    hpPerSecond: finite.optional(),
    pulse: z
        .object({
        interval: finite,
        damage: finite,
        radius: finite,
        slowScale: finite.optional(),
        slowSeconds: finite.optional(),
    })
        .strict()
        .optional(),
    detonate: z.object({ damage: finite, radius: finite, knockUp: finite }).strict().optional(),
})
    .strict();
export const DeployableSchema = z
    .object({
    id: nonNegInt,
    kind: DeployableKindSchema,
    slot: SlotSchema,
    position: Vec3Schema,
    yaw: finite,
    hp: finite,
    maxHp: finite,
    ticksLeft: nonNegInt,
    params: DeployableParamsSchema,
    timer: finite,
})
    .strict();
export const BotStateSchema = z
    .object({
    level: BotLevelSchema,
    mode: BotModeSchema,
    decisionTicks: nonNegInt,
    perceived: Vec3Schema.nullable(),
    seen: z.boolean(),
    seenTicks: nonNegInt,
    strafeDir: z.union([z.literal(1), z.literal(-1)]),
    aimYaw: finite,
    aimPitch: finite,
    noiseYaw: finite,
    noisePitch: finite,
    coverTarget: Vec3Schema.nullable(),
    stuckTicks: nonNegInt,
    lastPosition: Vec3Schema,
    jumpTicks: nonNegInt,
    holdFireTicks: nonNegInt,
})
    .strict();
export const CombatantSchema = z
    .object({
    slot: SlotSchema,
    mechId: z.string().min(1),
    modules: z.array(z.string().min(1)),
    body: MechStateSchema,
    hp: finite,
    shield: finite,
    shieldDelayTicks: nonNegInt,
    heat: finite,
    overheatTicks: nonNegInt,
    venting: z.boolean(),
    legDamage: finite,
    armDamage: finite,
    weapons: z.array(WeaponRuntimeSchema),
    melee: MeleeRuntimeSchema,
    abilities: z.tuple([AbilityRuntimeSchema, AbilityRuntimeSchema]),
    ultCharge: finite,
    effects: z.array(StatusEffectSchema),
    bot: BotStateSchema.nullable(),
    lastInput: MechInputSchema,
    alive: z.boolean(),
    platform: nonNegInt.nullable(),
})
    .strict();
export const ArenaRuntimeSchema = z
    .object({
    pickups: z.array(z.object({ respawnTick: nonNegInt }).strict()),
    pads: z.array(z.object({ readyTick: z.tuple([nonNegInt, nonNegInt]) }).strict()),
})
    .strict();
export const CombatantStatsSchema = z
    .object({
    damageDealt: finite,
    damageTaken: finite,
    selfDamage: finite,
    shotsFired: nonNegInt,
    shotsHit: nonNegInt,
    kills: nonNegInt,
    deaths: nonNegInt,
    roundsWon: nonNegInt,
    timeBonusTicks: nonNegInt,
    firstBlood: z.boolean(),
    abilitiesUsed: nonNegInt,
    ultimatesUsed: nonNegInt,
})
    .strict();
export const FlowStateSchema = z
    .object({
    phase: FlowPhaseSchema,
    round: int.positive(),
    ticksLeft: nonNegInt,
    wins: z.tuple([nonNegInt, nonNegInt]),
    roundWinner: SlotSchema.nullable(),
    swapped: z.boolean(),
    liveTick: nonNegInt,
})
    .strict();
export const RoundRecordSchema = z
    .object({
    round: int.positive(),
    winner: SlotSchema.nullable(),
    reason: RoundEndReasonSchema,
    ticks: nonNegInt,
    hpPct: z.tuple([finite, finite]),
})
    .strict();
export const ScoreBreakdownSchema = z
    .object({
    damageDealt: finite,
    roundWins: finite,
    matchWin: finite,
    accuracyBonus: finite,
    timeBonus: finite,
    firstBlood: finite,
    selfDamage: finite,
    total: finite,
    xp: finite,
    credits: finite,
    accuracy: finite,
})
    .strict();
export const MatchResultSchema = z
    .object({
    winner: SlotSchema.nullable(),
    reason: z.enum(['rounds', 'forfeit']),
    scores: z.tuple([ScoreBreakdownSchema, ScoreBreakdownSchema]),
})
    .strict();
const weaponId = z.string().min(1);
export const MatchEventSchema = z.discriminatedUnion('t', [
    z.object({ t: z.literal('roundStart'), round: int }).strict(),
    z.object({ t: z.literal('live'), round: int }).strict(),
    z
        .object({
        t: z.literal('shot'),
        slot: SlotSchema,
        weapon: weaponId,
        origin: Vec3Schema,
        end: Vec3Schema,
        hit: SlotSchema.nullable(),
        blocked: z.boolean(),
    })
        .strict(),
    z
        .object({
        t: z.literal('projectileSpawn'),
        id: int,
        slot: SlotSchema,
        weapon: weaponId,
        position: Vec3Schema,
        velocity: Vec3Schema,
    })
        .strict(),
    z
        .object({
        t: z.literal('projectileImpact'),
        id: int,
        position: Vec3Schema,
        normal: Vec3Schema.nullable(),
        hit: SlotSchema.nullable(),
    })
        .strict(),
    z
        .object({
        t: z.literal('beam'),
        slot: SlotSchema,
        weapon: weaponId,
        origin: Vec3Schema,
        end: Vec3Schema,
        hit: SlotSchema.nullable(),
    })
        .strict(),
    z
        .object({
        t: z.literal('hit'),
        target: SlotSchema,
        source: SlotSchema,
        kind: DamageKindSchema,
        hp: finite,
        shield: finite,
        location: HitLocationSchema,
        rear: z.boolean(),
        crit: z.boolean(),
        position: Vec3Schema,
    })
        .strict(),
    z
        .object({
        t: z.literal('blocked'),
        target: SlotSchema,
        source: SlotSchema,
        position: Vec3Schema,
    })
        .strict(),
    z.object({ t: z.literal('kill'), victim: SlotSchema, killer: SlotSchema.nullable() }).strict(),
    z
        .object({ t: z.literal('explosion'), slot: SlotSchema, position: Vec3Schema, radius: finite })
        .strict(),
    z
        .object({
        t: z.literal('melee'),
        slot: SlotSchema,
        weapon: weaponId,
        position: Vec3Schema,
        hit: SlotSchema.nullable(),
    })
        .strict(),
    z
        .object({
        t: z.literal('ability'),
        slot: SlotSchema,
        index: z.union([z.literal(0), z.literal(1)]),
        id: z.string().min(1),
        position: Vec3Schema,
    })
        .strict(),
    z
        .object({
        t: z.literal('effectStart'),
        slot: SlotSchema,
        kind: z.string().min(1),
        id: int,
        ticks: int,
    })
        .strict(),
    z
        .object({ t: z.literal('effectEnd'), slot: SlotSchema, kind: z.string().min(1), id: int })
        .strict(),
    z.object({ t: z.literal('overheat'), slot: SlotSchema }).strict(),
    z.object({ t: z.literal('vent'), slot: SlotSchema, on: z.boolean() }).strict(),
    z.object({ t: z.literal('shieldBroken'), slot: SlotSchema }).strict(),
    z
        .object({ t: z.literal('reload'), slot: SlotSchema, weapon: weaponId, seconds: finite })
        .strict(),
    z
        .object({
        t: z.literal('deployableSpawn'),
        id: int,
        kind: DeployableKindSchema,
        slot: SlotSchema,
        position: Vec3Schema,
        yaw: finite,
    })
        .strict(),
    z
        .object({
        t: z.literal('deployableDestroyed'),
        id: int,
        kind: DeployableKindSchema,
        position: Vec3Schema,
    })
        .strict(),
    z.object({ t: z.literal('parry'), slot: SlotSchema, position: Vec3Schema }).strict(),
    z.object({ t: z.literal('ultCharged'), slot: SlotSchema }).strict(),
    z
        .object({
        t: z.literal('pickup'),
        slot: SlotSchema,
        id: z.string().min(1),
        kind: z.enum(PICKUP_KINDS),
    })
        .strict(),
    z.object({ t: z.literal('padLaunch'), slot: SlotSchema, id: z.string().min(1) }).strict(),
    z
        .object({
        t: z.literal('roundEnd'),
        round: int,
        winner: SlotSchema.nullable(),
        reason: RoundEndReasonSchema,
    })
        .strict(),
    z.object({ t: z.literal('suddenDeath'), round: int }).strict(),
    z.object({ t: z.literal('matchEnd'), winner: SlotSchema.nullable() }).strict(),
]);
export const MatchStateSchema = z
    .object({
    version: z.literal(1),
    tick: nonNegInt,
    seed: int,
    rng: z.tuple([int, int, int, int]),
    arenaId: z.string().min(1),
    config: MatchConfigSchema,
    spawns: z.tuple([SpawnPointSchema, SpawnPointSchema]),
    flow: FlowStateSchema,
    mechs: z.tuple([CombatantSchema, CombatantSchema]),
    projectiles: z.array(ProjectileSchema),
    deployables: z.array(DeployableSchema),
    nextId: nonNegInt,
    stats: z.tuple([CombatantStatsSchema, CombatantStatsSchema]),
    firstBlood: SlotSchema.nullable(),
    rounds: z.array(RoundRecordSchema),
    events: z.array(MatchEventSchema),
    result: MatchResultSchema.nullable(),
    features: ArenaRuntimeSchema,
})
    .strict();
export function parseMatchState(json) {
    return MatchStateSchema.parse(json);
}
//# sourceMappingURL=schemas.js.map